/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Video from 'twilio-video';
import { connect } from 'react-redux';

import Fullscreen from '../../../global/Fullscreen';
import localeMessage from '../localeMessage';
import FullscreenIcon from './full.png';
import {
  initRoom,
  toggleCamera,
  toggleMic,
  networkColor,
  networkQuality,
  printNetworkQualityStats,
  detectHardware,
  handleTokenExpiry,
  handleReconnection,
  teardownRoom,
  getIdentityRole,
  handleConnectionError,
} from '../../twilioHelpers';
import { API_URL, COUNTRY } from '../../../../environment';
import { headers } from '../../../../actions/defaults';

const INITIAL_STATE = {
  cameraOpen: true,
  audioOpen: true,
  fullScreen: false,
  participantIsVoice: false,
  networkQualityLevel: '',
  partnerPresent: false,
  practitionerPresent: false,
  connectionError: '',
  connectionErrorCode: '',
  twilioVoiceToken: '',
  showYouAreNowConnectedMessage: false,
  isReconnecting: false,
  connectionLost: false,
  localAudioTrackId: 'initial',
  localVideoTrackId: 'initial',
};

function Screen(props) {
  const {
    channel,
    sessionType,
    locale,
    couplesSameLocation = false,
    appointment,
  } = props;

  const [state, setReactState] = useState(INITIAL_STATE);
  const pendingCallbacksRef = useRef([]);
  const prevLocalAudioTrackIdRef = useRef('initial');
  const selfRef = useRef(null);

  if (!selfRef.current) {
    selfRef.current = {
      state: INITIAL_STATE,
      props,
      channel,
      reconnectAttempts: 0,
      setState(updater, callback) {
        setReactState(prev => {
          const partial =
            typeof updater === 'function' ? updater(prev) : updater;
          return { ...prev, ...partial };
        });
        if (callback) {
          pendingCallbacksRef.current.push(callback);
        }
      },
    };
  }

  const self = selfRef.current;
  self.state = state;
  self.props = props;

  useEffect(() => {
    const cbs = pendingCallbacksRef.current.splice(0);
    cbs.forEach(cb => cb());
  });

  const fetchToken = () => {
    const {
      userId: uid,
      couplesSameLocation: sameLocation,
    } = self.props;

    return axios
      .get(
        `${API_URL}/practice/twilio_videos/token?room_id=${self.channel}&user_id=${uid}&same_location=${sameLocation}`,
        headers(),
      )
      .then(results => results.data);
  };

  const reconnectAfterTokenExpiry = () => {
    // Bounded retry guards against a loop if the backend keeps returning an
    // unusable token; reset on every successful (re)connect in connectToRoom.
    if (self.reconnectAttempts >= 3) return;
    self.reconnectAttempts += 1;

    fetchToken()
      .then(data => {
        if (data && data.token) {
          self.setState({ twilioVoiceToken: data.voice_token });
          connectToRoom(data.token);
        }
      })
      .catch(() => {});
  };

  const connectToRoom = token => {
    const {
      sessionType: currentSessionType,
      couplesSameLocation: sameLocation,
      appointment: currentAppointment,
      callbackForTwillio: callback,
    } = self.props;

    return Video.connect(token, {
      networkQuality: {
        local: 1, // LocalParticipant's Network Quality verbosity [1 - 3]
        remote: 2, // RemoteParticipants' Network Quality verbosity [0 - 3]
      },
      audio: false,
    }).then(room => {
      self.reconnectAttempts = 0;

      // Clear any stale local video left over from a previous connection so a
      // token-expiry reconnect doesn't stack duplicate local tiles.
      const localMediaContainer = document.getElementById('local-media');
      if (localMediaContainer) localMediaContainer.innerHTML = '';

      initRoom.bind(self)(room);

      room.localParticipant.setNetworkQualityConfiguration({
        local: 2,
        remote: 1,
      });

      if (currentAppointment) {
        if (currentAppointment.modality === 'Video') {
          if (
            room.localParticipant.audioTracks.size > 0 &&
            room.localParticipant.videoTracks.size > 0 &&
            room.state === 'connected'
          ) {
            self.setState({ showYouAreNowConnectedMessage: true });
          }
        } else if (currentAppointment.modality === 'Voice') {
          if (
            room.localParticipant.audioTracks.size > 0 &&
            room.state === 'connected'
          ) {
            self.setState({ showYouAreNowConnectedMessage: true });
          }
        }
      }
      if (self.state.showYouAreNowConnectedMessage) {
        setTimeout(() => {
          self.setState({ showYouAreNowConnectedMessage: false });
        }, 3000);
      }

      room.participants.forEach(participant => {
        if (currentSessionType === 'couples' && !sameLocation) {
          if (
            getIdentityRole(participant.identity) === 'guest' ||
            getIdentityRole(participant.identity) === 'host'
          ) {
            self.setState({
              partnerPresent: true,
            });
          } else if (getIdentityRole(participant.identity) === 'provider') {
            self.setState({
              practitionerPresent: true,
            });
          }
        } else if (participant) {
          self.setState({
            practitionerPresent: true,
          });
        }
      });

      printNetworkQualityStats.bind(self)(
        room.localParticipant.networkQualityLevel,
        room.localParticipant.networkQualityStats,
      );

      room.on('participantDisconnected', participant => {
        self.setState({
          room: self.state.room,
        });
        if (currentSessionType === 'couples' && !sameLocation) {
          if (
            getIdentityRole(participant.identity) === 'guest' ||
            getIdentityRole(participant.identity) === 'host'
          ) {
            self.setState({
              partnerPresent: false,
            });
          } else if (getIdentityRole(participant.identity) === 'provider') {
            self.setState({
              practitionerPresent: false,
            });
          }
        } else {
          self.setState({
            practitionerPresent: false,
          });
        }
      });

      room.on('participantConnected', participant => {
        self.setState({
          room: self.state.room,
        });
        if (currentSessionType === 'couples' && !sameLocation) {
          if (
            getIdentityRole(participant.identity) === 'guest' ||
            getIdentityRole(participant.identity) === 'host'
          ) {
            self.setState({
              partnerPresent: true,
            });
          } else if (getIdentityRole(participant.identity) === 'provider') {
            self.setState({
              practitionerPresent: true,
            });
          }
        } else {
          self.setState({
            practitionerPresent: true,
          });
        }
      });

      room.localParticipant.on(
        'networkQualityLevelChanged',
        printNetworkQualityStats.bind(self),
      );

      handleTokenExpiry(room, () => reconnectAfterTokenExpiry());

      handleReconnection(
        room,
        () => self.setState({ isReconnecting: true }),
        () => self.setState({ isReconnecting: false }),
        () => self.setState({ connectionLost: true }),
      );

      callback({
        toggleCamera: toggleCamera.bind(self),
        toggleMic: toggleMic.bind(self),
      });
    });
  };

  useEffect(() => {
    detectHardware.bind(self)();
    self.reconnectAttempts = 0;

    fetchToken()
      .then(data => {
        self.setState({ twilioVoiceToken: data.voice_token });
        return connectToRoom(data.token);
      })
      .catch(handleConnectionError.bind(self));

    return () => {
      teardownRoom(self.state.room);
    };
    // Match class mount/unmount token + room lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { localAudioTrackId, localVideoTrackId } = state;
    const prevLocalAudioTrackId = prevLocalAudioTrackIdRef.current;

    if (prevLocalAudioTrackId === 'initial') {
      if (prevLocalAudioTrackId !== localAudioTrackId) {
        // For video sessions, 'you are now connected' message will be displayed when valid localAudioTrackId && localVideoTrackId sent from twilioHelper.js
        if (appointment.modality === 'Video') {
          if (
            localAudioTrackId !== 'initial' &&
            localVideoTrackId !== 'initial'
          ) {
            self.setState({ showYouAreNowConnectedMessage: true }, () => {
              // 'you are now connected' message will be displayed for 3 seconds.
              setTimeout(() => {
                self.setState({ showYouAreNowConnectedMessage: false });
              }, 3000);
            });
          }
          // For voice sessions, 'you are now connected' message will be displayed when valid localAudioTrackId && localVideoTrackId sent from twilioHelper.js
        } else if (appointment.modality === 'Voice') {
          if (localAudioTrackId !== 'initial') {
            self.setState({ showYouAreNowConnectedMessage: true }, () => {
              // 'you are now connected' message will be displayed for 3 seconds.
              setTimeout(() => {
                self.setState({ showYouAreNowConnectedMessage: false });
              }, 3000);
            });
          }
        }
      }
    }

    prevLocalAudioTrackIdRef.current = localAudioTrackId;
  }, [state.localAudioTrackId, state.localVideoTrackId, appointment.modality, self]);

  const toggleFullScreen = () => {
    self.setState(prevState => ({
      fullScreen: !prevState.fullScreen,
    }));
  };

  const renderUnableMessage = () => {
    const { networkQualityLevel, hardwareErrorCode, connectionErrorCode } =
      state;

    return (
      <p
        className={
          hardwareErrorCode || connectionErrorCode
            ? 'unable-connect-message unable-connect-message--error'
            : 'unable-connect-message'
        }
      >
        {localeMessage('unableTo', locale)}
        <b
          onClick={() => window.location.reload()}
          style={{ textDecoration: 'underline' }}
        >
          {localeMessage('here', locale)}
        </b>
        <span
          className="network-quality"
          style={{ color: networkColor(networkQualityLevel) }}
        >
          {networkQuality[networkQualityLevel]}
        </span>
        <span
          style={{
            fontWeight: 'bold',
            display: 'block',
            marginTop: '20px',
          }}
        >
          {localeMessage(hardwareErrorCode, locale) ||
            (connectionErrorCode && localeMessage('connectionError', locale))}
        </span>
      </p>
    );
  };

  const renderPhoneNumber = () => {
    if (locale === 'en') {
      return COUNTRY === 'US'
        ? `+1 833 934 2673 and use pin ${state.twilioVoiceToken} followed by #`
        : `+1 833 461 0480 and use pin ${state.twilioVoiceToken} followed by #`;
    }
    return `+1 833 461 0480 et utilisez le NIP 00016049 suivie par #`;
  };

  const renderCouplesRemotes = () => {
    if (sessionType !== 'couples' || couplesSameLocation) {
      return <div />;
    }

    return (
      <div className="remote-couples">
        <div id="remote-provider-media" className="remote-couples-media">
          {state.practitionerPresent === true ? (
            <h1>
              {localeMessage('yourProviderIssue', locale)}
              <br></br>
              {localeMessage('uniqueDialInCode', locale)}
              {renderPhoneNumber()}
              {localeMessage('contactSupport', locale)}
            </h1>
          ) : (
            <h1>{localeMessage('yourCare', locale)}</h1>
          )}
        </div>
        <div id="remote-partner-media" className="remote-couples-media">
          {state.partnerPresent === true ? (
            <h1>
              {localeMessage('yourPartnerIssue', locale)}
              <br></br>
              {localeMessage('uniqueDialInCode', locale)}
              {renderPhoneNumber()}
              {localeMessage('contactSupport', locale)}
            </h1>
          ) : (
            <h1>{localeMessage('yourCare', locale)}</h1>
          )}
        </div>
      </div>
    );
  };

  const { practitionerPresent } = state;

  return (
    <div className="not-connected-screen">
      <Fullscreen
        enabled={state.fullScreen}
        onChange={isFull => self.setState({ fullScreen: isFull })}
      >
        <div id="remote-media-div" className="full-screenable-node">
          <h1
            style={{
              display:
                sessionType === 'couples' && !couplesSameLocation
                  ? 'none'
                  : 'block',
            }}
          >
            {practitionerPresent ? (
              <h1>
                {localeMessage('yourProviderIssue', locale)}
                <br></br>
                {localeMessage('uniqueDialInCode', locale)}
                {renderPhoneNumber()}
                {localeMessage('contactSupport', locale)}
              </h1>
            ) : (
              localeMessage('yourCare', locale)
            )}
          </h1>
          {renderCouplesRemotes()}
          <div id="local-media" />
          <img
            role="presentation"
            src={FullscreenIcon}
            alt="full-screen"
            className="full-screen-icon"
            onClick={toggleFullScreen}
          />
          {state.showYouAreNowConnectedMessage && (
            <div className="you-are-now-connected-message-box">
              <p>{localeMessage('youAreNowConnected', locale)}</p>
            </div>
          )}
          {state.isReconnecting && (
            <div className="reconnecting-message-box">
              <p>{localeMessage('reconnecting', locale)}</p>
            </div>
          )}
          {state.connectionLost && (
            <div className="reconnecting-message-box">
              <p>{localeMessage('connectionLost', locale)}</p>
            </div>
          )}
          {renderUnableMessage()}
        </div>
      </Fullscreen>
    </div>
  );
}

Screen.propTypes = {
  callbackForTwillio: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  channel: PropTypes.string.isRequired,
  sessionType: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  couplesSameLocation: PropTypes.bool,
  appointment: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    locale: (state.user && state.user.locale) || 'en',
  };
}

export default connect(mapStateToProps)(Screen);
